import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { fetchBrand } from 'apis/enterprise/api';
import { Brand } from 'apis/enterprise/types';
import { RouteWhenAuthenticated } from 'common/components';
import PageLoadError from 'core/PageLoadError';
import BrandUserManagement from 'modules/access-control/BrandUserManagement';
import BrandPurchaseData from 'modules/brand/BrandPurchaseData';
import BrandReporting from 'modules/brand/components/brand-reporting';
import { DetailVenueGroup, ListingVenueGroup } from 'modules/brand/components/venue-group';
import { BrandContextProvider } from 'modules/brand/context/BrandContext';
import { useIsVenueGroupEnabled } from 'modules/brand/hooks/useIsVenueGroupEnabled';
import { getBrandRoute } from 'modules/brand/utils/getBrandRoute';
import HistoricalVenueAvailbility from 'modules/historical-venue-availability/HistoricalVenueAvailability';
import FeatureBrandInsights from 'modules/insights-v2/feature/FeatureBrandInsights';
import useInsightV2FeatureFlag from 'modules/insights-v2/hooks/useInsightV2FeatureFlag';
import BrandPayouts from 'modules/payout-reports/BrandPayouts';
import { BrandSharedMenuRoot } from 'modules/shared-menu/SharedMenuRoutes';
import { useIsBrandSharedMenuEnabled } from 'modules/shared-menu/hooks/useIsSharedMenuEnabled';
import { SidebarNavigationBrandTemplate } from 'modules/sidebar/SidebarNavigationTemplate';
import VenueAvailabilityStatus from 'modules/venue-availability-status';

import { useTelemetryBrandMetadata } from '../../modules/brand/hooks/telemetry';

type Params = {
  brandId: string;
  slug: string;
};

type Props = {
  brand: Brand;
};

const BrandRoutes = ({ brand }: Props) => {
  useTelemetryBrandMetadata();
  const { path } = useRouteMatch<{ slug: string }>();
  const { name: brandName, id: brandId, enterpriseId, country: brandCountry } = brand;
  const purchaseDataRoute = getBrandRoute('purchase-data', enterpriseId, brandId);
  const isSharedMenuEnabled = useIsBrandSharedMenuEnabled(brand.id);

  const { isInsightV2Enabled } = useInsightV2FeatureFlag('brand', brand.id);
  const isVenueGroupEnabled = useIsVenueGroupEnabled(brand.country, brand.id);

  const VenueAvailabilityStatusRoot = useMemo(
    () => () => <VenueAvailabilityStatus entity={{ type: 'brand', brand }} />,
    [brand],
  );

  const HistoricalVenueAvailabilityRoot = useMemo(
    () => () => <HistoricalVenueAvailbility entity={{ type: 'brand', brand }} />,
    [brand],
  );
  return (
    <SidebarNavigationBrandTemplate
      brandName={brandName}
      brandId={brandId}
      enterpriseId={enterpriseId}
      brandCountry={brandCountry}
    >
      <Switch>
        {/* Todo: Will remove after we have the brand index page */}
        <RouteWhenAuthenticated exact path={path}>
          <Redirect to={purchaseDataRoute} />
        </RouteWhenAuthenticated>
        <RouteWhenAuthenticated path={`${path}/purchase-data`} component={BrandPurchaseData} />
        {isInsightV2Enabled && <RouteWhenAuthenticated path={`${path}/insights`} component={FeatureBrandInsights} />}
        {isVenueGroupEnabled && (
          <RouteWhenAuthenticated exact path={`${path}/venues/groups`} component={ListingVenueGroup} />
        )}
        {isVenueGroupEnabled && (
          <RouteWhenAuthenticated exact path={`${path}/venues/groups/:venueGroupId`} component={DetailVenueGroup} />
        )}

        <RouteWhenAuthenticated path={`${path}/reporting/payout`} component={BrandPayouts} />

        <RouteWhenAuthenticated
          exact
          path={`${path}/venues/historical-venue-availability`}
          component={HistoricalVenueAvailabilityRoot}
        />

        <RouteWhenAuthenticated path={`${path}/venues`} component={VenueAvailabilityStatusRoot} />

        <RouteWhenAuthenticated path={`${path}/reporting`} component={BrandReporting} />

        <RouteWhenAuthenticated path={`${path}/access`} component={BrandUserManagement} />

        {isSharedMenuEnabled && <RouteWhenAuthenticated path={`${path}/shared-menu`} component={BrandSharedMenuRoot} />}
      </Switch>
    </SidebarNavigationBrandTemplate>
  );
};

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

export default () => {
  const { brandId } = useParams<Params>();
  const {
    data: brand,
    isLoading: isLoadingBrand,
    isFetching,
  } = useQuery(
    [`/v1/brands/${brandId}`],
    () => {
      return brandId ? fetchBrand(brandId) : undefined;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const isLoading = isLoadingBrand && isFetching;

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" />
      </SpinnerContainer>
    );
  }

  if (brand) {
    return (
      <BrandContextProvider value={{ brand }}>
        <BrandRoutes brand={brand} />
      </BrandContextProvider>
    );
  }

  return <PageLoadError />;
};
